import { cn } from "@/components/podkit/lib/cn";
import type { WithTrackingId } from "@/types/tracking";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes, type ReactElement } from "react";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleInfo } from "central-icons/IconCircleInfo";
import { IconLoader } from "central-icons/IconLoader";
import { Button } from "@/components/flexkit/Button";

const bannerVariant = cva(
    cn("flex justify-center items-center gap-2 rounded-lg text-base border px-3 py-2 shadow-sm"),
    {
        variants: {
            variant: {
                danger: "bg-surface-banner-destructive-subtle text-content-destructive border-border-error",
                warning: "bg-surface-banner-warning-subtle text-content-warning border-border-warning",
                success: "bg-surface-banner-success-subtle text-content-success border-border-success",
                info: "bg-surface-muted text-content-muted border-border-base",
                progress: "bg-surface-muted text-content-muted border-border-base",
            },
        },
        defaultVariants: {
            variant: "danger",
        },
    },
);

export type BannerVariant = VariantProps<typeof bannerVariant>["variant"];

export type BannerAction = {
    text: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    className?: string;
    /** Enable responsive behavior: button wraps to new line on mobile */
    responsive?: boolean;
} & WithTrackingId;

export type BannerProps = {
    text: string | ReactElement;
    truncate?: boolean;
    LeadingIcon?: ReactElement | null;
    action?: BannerAction;
    link?: ReactElement;
} & VariantProps<typeof bannerVariant> &
    HTMLAttributes<HTMLDivElement>;

export const Banner = forwardRef<HTMLDivElement, BannerProps>(
    ({ text, variant, className, truncate, LeadingIcon, action, link, ...props }, ref) => {
        const hasAction = !!action && !!action.text?.trim() && typeof action.onClick === "function";
        const hasLink = !!link;

        const getDefaultIcon = () => {
            switch (variant) {
                case "danger":
                    return <IconTriangleExclamation size={16} className={cn("text-content-destructive", "shrink-0")} />;
                case "warning":
                    return <IconTriangleExclamation size={16} className={cn("text-content-warning", "shrink-0")} />;
                case "success":
                    return <IconCircleCheck size={16} className={cn("text-content-success", "shrink-0")} />;
                case "info":
                    return <IconCircleInfo size={16} className={cn("text-content-muted", "shrink-0")} />;
                case "progress":
                    return <IconLoader size={16} className={cn("shrink-0 animate-spin text-content-muted")} />;
                default:
                    return <IconTriangleExclamation size={16} className={cn("text-content-warning", "shrink-0")} />;
            }
        };

        let icon;
        if (LeadingIcon === null) {
            icon = null;
        } else if (LeadingIcon !== undefined) {
            icon = <span className="shrink-0">{LeadingIcon}</span>;
        } else {
            icon = getDefaultIcon();
        }

        return (
            <div
                ref={ref}
                data-testid="banner"
                className={cn(
                    bannerVariant({ variant }),
                    {
                        "overflow-hidden": truncate,
                        "justify-between": hasAction,
                        "justify-center": !hasAction,
                    },
                    hasAction && action.responsive && "flex-wrap",
                    className,
                )}
                {...props}
            >
                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden pr-2">
                    {icon}
                    <div
                        data-testid="banner-text"
                        className={cn("min-w-0", {
                            truncate: truncate,
                            "break-words": !truncate,
                        })}
                        title={typeof text === "string" && truncate ? text : undefined}
                    >
                        {text}
                    </div>
                    {hasLink && <div className="shrink-0">{link}</div>}
                </div>
                {hasAction && (
                    <div className={cn(
                        "shrink-0",
                        action.responsive && "mt-2 w-full sm:mt-0 sm:w-auto"
                    )}>
                        <Button
                            data-testid="banner-action-button"
                            variant="outline"
                            size="md"
                            className={cn(
                                "border-current text-inherit hover:text-inherit",
                                action.responsive && "w-full sm:w-auto",
                                action.className
                            )}
                            onClick={action.onClick}
                            disabled={action.disabled}
                            loading={action.loading}
                            _disableTranslateWrapping={true}
                            data-tracking-id={action["data-tracking-id"]}
                        >
                            {action.text}
                        </Button>
                    </div>
                )}
            </div>
        );
    },
);
Banner.displayName = "Banner";
